/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Grid2 as Grid,
  Button,
  CircularProgress,
} from "@mui/material";
import type { TemplateInformation, FieldTemplate } from "../types/api-types";
import ExportCsvButton from "../../shared/export/export-csv-button";
import type { CsvColumn } from "../../shared/export/csv-export";

const exportColumns: CsvColumn<FieldTemplate>[] = [
  { header: "Field", value: (row) => row.field.columnName },
  { header: "Template", value: (row) => row.template?.description },
];

interface FieldTemplatesTableProps {
  fieldTemplates: FieldTemplate[];
  templates: TemplateInformation[] | undefined;
  isLoading?: boolean;
  isSaving?: boolean;
  onTemplateChange: (
    columnId: number,
    newTemplate: TemplateInformation | null,
  ) => void;
  onSave: () => void;
}

const FieldTemplatesTable = ({
  fieldTemplates,
  templates,
  isLoading = false,
  isSaving = false,
  onTemplateChange,
  onSave,
}: FieldTemplatesTableProps) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      border: 1,
      borderColor: "divider",
      borderRadius: 2,
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        boxShadow: 2,
      },
    }}
  >
    <Grid container spacing={1.5}>
      <Grid size={12}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 0.5,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            Field Templates
          </Typography>
          <ExportCsvButton
            rows={isLoading ? [] : fieldTemplates}
            columns={exportColumns}
            fileName="field-templates"
          />
        </Box>
        <Divider sx={{ mb: 1 }} />
      </Grid>

      <Grid size={12}>
        <Box
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: 1,
            p: 1.5,
          }}
        >
          {isLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!isLoading && fieldTemplates.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No data found
            </Typography>
          )}
          {!isLoading && fieldTemplates.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        fontWeight: 600,
                        color: "primary.main",
                        bgcolor: "grey.50",
                      },
                    }}
                  >
                    <TableCell>Field</TableCell>
                    <TableCell>Template</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fieldTemplates.map((row) => (
                    <TableRow
                      key={row.field.columnId}
                      sx={{
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                        "&:last-child td": {
                          borderBottom: 0,
                        },
                      }}
                    >
                      <TableCell sx={{ py: 1.5 }}>
                        {row.field.columnName}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Autocomplete
                          options={templates || []}
                          getOptionLabel={(option) => option.description}
                          value={row.template}
                          onChange={(_e, newValue) =>
                            onTemplateChange(row.field.columnId, newValue)
                          }
                          size="small"
                          disabled={isSaving}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select template..."
                            />
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Grid>

      <Grid size={12}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={onSave}
            disabled={isSaving || fieldTemplates.length === 0}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </Box>
      </Grid>
    </Grid>
  </Paper>
);

export default FieldTemplatesTable;
